import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileText, AlertCircle, CheckCircle2, Clock,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// MyPoliciesScreen — /hr/policies
// T-121 phase 1, chunk A — list view of pending + acknowledged policies for
// the logged-in user. Reads from v_policy_pending_for_user (added in
// migration 071), filtered to the current user.
//
// Sections:
//   • Action required — ack_status in (never_signed | expired_annual |
//     major_revision_pending). Drives the red badge on the nav (next chunk).
//   • Acknowledged & current — ack_status = 'current'.
//
// The reader + signature flow lands in chunk B. Rows are not yet clickable.
// =============================================================================

type AckStatus =
  | "current"
  | "never_signed"
  | "expired_annual"
  | "major_revision_pending";

interface PendingRow {
  policy_id:               string;
  policy_code:             string;
  display_name:            string;
  policy_category:         string | null;
  current_version_label:   string | null;
  current_file_id:         string | null;
  ack_status:              AckStatus;
  last_ack_at:             string | null;
  last_ack_expires_at:     string | null;
}

const STATUS_PILL: Record<AckStatus, { label: string; tone: any; icon: any }> = {
  never_signed:           { label: "Never signed",  tone: "lost",    icon: AlertCircle  },
  expired_annual:         { label: "Annual due",    tone: "warning", icon: Clock        },
  major_revision_pending: { label: "Re-ack needed", tone: "warning", icon: AlertCircle  },
  current:                { label: "Current",       tone: "ok",      icon: CheckCircle2 },
};

// -----------------------------------------------------------------------------

export default function MyPoliciesScreen() {
  const user = useRequireAuth();

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["my_policies", user?.user_id],
    enabled:  !!user?.user_id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from("v_policy_pending_for_user")
        .select(`
          policy_id, policy_code, display_name, policy_category,
          current_version_label, current_file_id,
          ack_status, last_ack_at, last_ack_expires_at
        `)
        .eq("user_id", user!.user_id)
        .order("ack_status")
        .order("policy_code");
      if (error) throw error;
      return (data ?? []) as unknown as PendingRow[];
    },
  });

  const { pending, current } = useMemo(() => {
    const p: PendingRow[] = [];
    const c: PendingRow[] = [];
    for (const r of rows) {
      if (r.ack_status === "current") c.push(r);
      else p.push(r);
    }
    return { pending: p, current: c };
  }, [rows]);

  if (isLoading) {
    return <div className="py-20 flex justify-center"><Spinner /></div>;
  }
  if (error) {
    return (
      <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Couldn't load policies: {String((error as any)?.message ?? error)}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">My Policies</h1>
        <div className="text-text-muted text-[13px] mt-1 max-w-[820px]">
          Coretel's controlled policies that apply to your role. Action-required
          items above need to be read and signed; current items are valid until
          their next annual re-acknowledgement date.
        </div>
      </header>

      {/* Action required */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[12px] font-medium text-text-muted uppercase tracking-wider">
            Action required
            <span className="text-text-dim ml-2">· {pending.length}</span>
          </h2>
        </div>
        <Card>
          {pending.length === 0 ? (
            <EmptyState
              title="You're all caught up"
              description="No policies need your attention right now."
              icon={CheckCircle2}
            />
          ) : (
            <ul className="divide-y divide-line">
              {pending.map(r => <PolicyRow key={r.policy_id} row={r} />)}
            </ul>
          )}
        </Card>
      </section>

      {/* Current */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[12px] font-medium text-text-muted uppercase tracking-wider">
            Acknowledged &amp; current
            <span className="text-text-dim ml-2">· {current.length}</span>
          </h2>
        </div>
        <Card>
          {current.length === 0 ? (
            <EmptyState
              title="Nothing acknowledged yet"
              description="Once you sign a policy, it'll appear here with its expiry date."
              icon={FileText}
            />
          ) : (
            <ul className="divide-y divide-line">
              {current.map(r => <PolicyRow key={r.policy_id} row={r} />)}
            </ul>
          )}
        </Card>
      </section>

      {/* Footnote — reader coming next */}
      <div className="mt-4 px-3 py-2 rounded border border-line text-[12px] text-text-muted bg-ink-raised">
        <strong className="text-text">Click a row to open the policy.</strong> The reader is live — the signature pad + final submit lands in T-121 chunk B3.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function PolicyRow({ row }: { row: PendingRow }) {
  const status = STATUS_PILL[row.ack_status];
  const StatusIcon = status.icon;

  // Right-side context: for current rows, show expiry countdown; for pending
  // rows, show the reason in plain English.
  const rightHint = (() => {
    if (row.ack_status === "current" && row.last_ack_expires_at) {
      return `expires ${fmtDate(row.last_ack_expires_at)}`;
    }
    if (row.ack_status === "expired_annual" && row.last_ack_expires_at) {
      return `expired ${fmtDate(row.last_ack_expires_at)}`;
    }
    if (row.ack_status === "major_revision_pending") {
      return "new version published";
    }
    return null;
  })();

  return (
    <Link to={`/policies/${row.policy_code}`} className="block hover:bg-ink-raised transition-colors">
    <li className="px-4 py-3 flex items-center gap-3">
      <FileText size={16} className="text-text-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-accent">{row.policy_code}</span>
          {row.current_version_label && (
            <span className="font-mono text-[11px] text-text-dim">
              {row.current_version_label}
            </span>
          )}
          <span className="font-medium truncate">{row.display_name}</span>
        </div>
        <div className="text-[11px] text-text-muted mt-0.5">
          {row.policy_category ?? "—"}
          {rightHint && <span> · {rightHint}</span>}
        </div>
      </div>
      <Pill label={status.label} tone={status.tone} />
      </li>
    </Link>
  );
}
